import { useEffect, useState } from "react";
import { ACTIONS, useSelfEvaluationInfoContext } from "../Form/SelfEvaluationInfoContext";
import { useSelfEvaluationMeta } from "./useSelfEvaluationMeta";
import { useGetAdditionalFieldValueBySelfEvalRequirementIdForAdminMutation, useGetFileListBySelfEvalRequirementIdForAdminMutation, useLazyGetMinimumCriteriaForServiceByApplicationIdQuery, useLazyGetSelfEvaluationRequirementBySelfEvaluationIdForAdminQuery } from "../../../../../../app/services/selfEvalutionReviewService/selfEvalutionReviewServiceApi";
import ToastService from "../../../../../../services/toastService/ToastService";
import { useApplicationId } from "./useApplicationId";

export function useSelfEvaluationInfoData() {
    const { dispatch } = useSelfEvaluationInfoContext();
    const applicationId = useApplicationId();
    const { selfEvaluationId, isLoading: isMetaLoading } = useSelfEvaluationMeta();
    const [isHydrating, setIsHydrating] = useState(true);
    const [isCriteriaLoading, setIsCriteriaLoading] = useState(true);

    const [getRequirements] = useLazyGetSelfEvaluationRequirementBySelfEvaluationIdForAdminQuery();
    const [getFileList] = useGetFileListBySelfEvalRequirementIdForAdminMutation();
    const [getFieldValues] = useGetAdditionalFieldValueBySelfEvalRequirementIdForAdminMutation();
    const [getMinimumCriteria] = useLazyGetMinimumCriteriaForServiceByApplicationIdQuery();

    useEffect(() => {
        if (!(applicationId > 0)) {
            setIsCriteriaLoading(false);
            return;
        }

        let ignore = false;
        setIsCriteriaLoading(true);

        (async () => {
            try {
                const res = await getMinimumCriteria(applicationId, true).unwrap();
                if (ignore) return;

                const value = Number(res?.minimumCriteria) || 0;
                dispatch({ type: ACTIONS.SET_SECTION_B_MIN_CRITERIA, value });
            } catch {
                if (!ignore) ToastService.error("Unable to load minimum criteria.");
            } finally {
                if (!ignore) setIsCriteriaLoading(false);
            }
        })();

        return () => {
            ignore = true;
        };
    }, [applicationId, getMinimumCriteria, dispatch]);

    useEffect(() => {
        if (isMetaLoading) return;
        if (!(selfEvaluationId > 0)) {
            setIsHydrating(false);
            return;
        }

        let ignore = false;
        setIsHydrating(true);

        (async () => {
            try {
                const items = await getRequirements(selfEvaluationId, true).unwrap();
                if (ignore || !items?.length) return;

                dispatch({ type: ACTIONS.SET_REQUIREMENTS, items });

                const payload = {
                    selfEvaluationId,
                    requirementIds: items.map((item) => item.selfEvalRequirementId),
                };
                const [files, values] = await Promise.all([
                    getFileList(payload).unwrap(),
                    getFieldValues(payload).unwrap(),
                ]);
                if (ignore) return;

                dispatch({ type: ACTIONS.SET_FILES, items: files ?? [] });
                dispatch({ type: ACTIONS.SET_FIELD_VALUES, items: values ?? [] });
            } catch {
                if (!ignore) ToastService.error("Unable to load submitted details.");
            } finally {
                if (!ignore) setIsHydrating(false); // success ke error, loader hatavo
            }
        })();

        return () => {
            ignore = true;
        };
    }, [isMetaLoading, selfEvaluationId, getRequirements, getFileList, getFieldValues, dispatch]);

    return { isLoading: isMetaLoading || isHydrating };
}